import { createClient } from '@supabase/supabase-js'
import logger from '@/lib/logger'

// Initialize Supabase client strictly via server endpoint
let supabaseClient: any = null;

/**
 * Fetch with retry and exponential backoff.
 * Handles Render free-plan cold starts where the server may take 30-60s to wake.
 */
async function fetchWithRetry(url: string, retries = 3, baseDelay = 1000): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);
      if (response.ok) return response;
      if (attempt === retries) {
        const text = await response.text().catch(() => '');
        throw new Error(`${url} failed with status ${response.status}: ${text}`);
      }
    } catch (error) {
      if (attempt === retries) throw error;
    }
    const delay = baseDelay * Math.pow(2, attempt);
    logger.info(`[supabase] Retrying ${url} in ${delay}ms (attempt ${attempt + 1}/${retries})`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  throw new Error(`${url} failed after ${retries + 1} attempts`);
}

const getSupabaseClient = async () => {
  if (supabaseClient) return supabaseClient;

  try {
    const response = await fetchWithRetry('/api/supabase-config');

    const config = await response.json();
    const url = config?.url;
    const anonKey = config?.anonKey;
    if (!url || !anonKey) {
      throw new Error('Supabase configuration missing url or anonKey from server');
    }

    supabaseClient = createClient(url, anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });

    return supabaseClient;
  } catch (error) {
    logger.error('[supabase] Initialization error:', error as any);
    throw new Error('Failed to initialize Supabase client via server endpoint');
  }
};

export { getSupabaseClient };